import { ref } from 'vue'
import type { SupabaseClient, RealtimeChannel } from '@supabase/supabase-js'
import { trackSupabaseChannelStatus } from '~/utils/reconnectTelemetry'

interface RealtimeState {
  isConnected: boolean
  isReconnecting: boolean
  reconnectAttempts: number
  error: string | null
}

type EventCallback<T = unknown> = (data: T) => void

/**
 * CLIENT-ONLY COMPOSABLE
 *
 * Module-level state is intentional for singleton pattern - ensures a single
 * shared state across all component instances using this composable.
 *
 * This composable relies on browser APIs (Supabase Realtime) and is only used in
 * client-side components. The Nuxt plugin ($supabase) handles SSR safety by
 * only initializing on the client.
 */
let channel: RealtimeChannel | null = null
let isSubscribed = false
const pendingListeners: Array<{ event: string; callback: EventCallback }> = []
// Store all registered listeners so they can be re-registered on channel recreation
const registeredListeners: Array<{ event: string; callback: EventCallback }> = []
const sharedState = ref<RealtimeState>({
  isConnected: false,
  isReconnecting: false,
  reconnectAttempts: 0,
  error: null,
})

/**
 * Supabase Realtime communication composable
 *
 * Provides reactive state and methods for Supabase Realtime broadcast communication.
 * Replaces Socket.IO useSocket composable with the same API surface.
 *
 * @example
 * ```ts
 * const { state, connect, on, off } = useSupabaseRealtime()
 *
 * onMounted(() => {
 *   connect()
 *   on('round:tick', (data) => console.log(data))
 * })
 * ```
 */
export function useSupabaseRealtime() {
  const { $supabase } = useNuxtApp()
  const supabase = $supabase as SupabaseClient

  const state = sharedState

  /**
   * Create the channel without subscribing
   * Call this first, then register listeners with on(), then call subscribe()
   */
  function createChannel(): void {
    // If channel already exists and is subscribed, skip
    if (channel && isSubscribed) {
      return
    }

    // Remove any existing channels with same name to avoid duplicates
    // This handles HMR and reconnection scenarios where Supabase client persists
    // but our module-level variables were reset
    const existingChannels = supabase.getChannels()
    for (const ch of existingChannels) {
      if (ch.topic === 'realtime:crash-game') {
        supabase.removeChannel(ch)
      }
    }

    // Reset state for fresh channel
    channel = null
    isSubscribed = false

    // Public-topic channel under private:true mode — anon can subscribe per
    // messages_public_topics_select policy, but client-side .send() is blocked
    // by messages_block_*_publish (only backend service_role can broadcast).
    channel = supabase.channel('crash-game', {
      config: {
        private: true, // §5.12 — closes the client-publish hole
        broadcast: { self: false }, // Don't receive own broadcasts
      },
    })

    // Move pending listeners to registered (without registering yet)
    for (const listener of pendingListeners) {
      registeredListeners.push(listener)
    }
    pendingListeners.length = 0

    // Register ALL listeners on the new channel
    for (const { event, callback } of registeredListeners) {
      channel.on('broadcast', { event }, ({ payload }) => {
        callback(payload)
      })
    }
  }

  /**
   * Subscribe to the channel (call after registering all listeners)
   */
  function subscribe(): void {
    if (!channel) {
      console.warn('[Realtime] Channel not created, call createChannel() first')
      return
    }
    if (isSubscribed) return

    isSubscribed = true
    channel.subscribe((status, err) => {
      if (status === 'SUBSCRIBED') {
        state.value.isConnected = true
        state.value.isReconnecting = false
        state.value.reconnectAttempts = 0
        state.value.error = null
        console.warn('[Realtime] Connected to crash-game channel')
      } else if (status === 'CHANNEL_ERROR') {
        state.value.error = err?.message || 'Failed to connect to game channel'
        console.warn('[Realtime] Channel error:', err)
      } else if (status === 'TIMED_OUT') {
        state.value.error = 'Connection timed out'
        state.value.isReconnecting = true
      } else if (status === 'CLOSED') {
        state.value.isConnected = false
        isSubscribed = false
      }
      // Unified reconnect telemetry (degraded → recovered duration + counts).
      trackSupabaseChannelStatus('crash-broadcast', status, err?.message)
    })
  }

  /**
   * Connect to the game channel (legacy method - creates channel and subscribes)
   * For proper listener registration, use createChannel() + on() + subscribe()
   */
  async function connect(): Promise<void> {
    createChannel()
    subscribe()
  }

  /**
   * Disconnect from the game channel
   */
  function disconnect(): void {
    if (channel) {
      supabase.removeChannel(channel)
      channel = null
      state.value.isConnected = false
      isSubscribed = false
      // Note: registeredListeners are preserved for reconnectWithAuth() to work
      // Memory accumulation is not a concern since on() deduplicates by event name
    }
  }

  /**
   * Reconnect with fresh authentication token
   * Call this after login or session restoration
   */
  async function reconnectWithAuth(): Promise<void> {
    disconnect()
    await connect()
  }

  /**
   * Listen for a broadcast event from the server
   * IMPORTANT: Call this BEFORE subscribe() for listeners to work properly
   */
  function on<T>(event: string, callback: (data: T) => void): void {
    const wrappedCallback: EventCallback = (data) => callback(data as T)

    // Check if listener for this event already registered (avoid duplicates)
    const alreadyRegistered = registeredListeners.some((l) => l.event === event)
    const alreadyPending = pendingListeners.some((l) => l.event === event)

    if (!channel) {
      // Store listener to be registered when channel is created
      if (!alreadyRegistered && !alreadyPending) {
        pendingListeners.push({ event, callback: wrappedCallback })
      }
      return
    }

    // Register listener on channel
    channel.on('broadcast', { event }, ({ payload }) => {
      callback(payload as T)
    })

    // Store for re-registration on channel recreation
    if (!alreadyRegistered) {
      registeredListeners.push({ event, callback: wrappedCallback })
    }
  }

  /**
   * Remove an event listener
   * Note: Supabase Realtime doesn't support removing individual listeners.
   * Listeners are cleaned up when channel is removed via disconnect().
   */
  function off(_event: string, _callback?: (...args: unknown[]) => void): void {
    // Supabase Realtime doesn't support removing individual listeners
    // Listeners are cleaned up when channel is removed
    console.warn('[Realtime] Individual listener removal not supported. Use disconnect() to clean up all listeners.')
  }

  /**
   * Initialize realtime - creates channel but does NOT subscribe
   * Use this for lazy initialization, then call subscribe() after setting up listeners
   */
  function _init(): void {
    createChannel()
  }

  return {
    state,
    connect,
    createChannel,
    subscribe,
    disconnect,
    reconnectWithAuth,
    on,
    off,
  }
}
